import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
// mantine
import { ActionIcon, Avatar, Table, UnstyledButton } from '@mantine/core';
import { modals } from '@mantine/modals';
// icons
import { Plus, Settings, HelpCircle } from 'lucide-react';
// components
import type { IIntegration } from '@newo/api/gateway';
import ConnectorItem from '@/pages/Integrations/components/ConnectorItem';
import SettingsForm from '@/pages/Integrations/components/SettingsForm';
import AddConnector from '@/pages/Integrations/components/AddConnector';
// utils
import {
    useIntegrationConnectorsQuery,
    useIntegrationSettingsQuery,
    useChangeIntegrationSettingsMutation,
    QUERY_KEYS
} from '@/services/integration/integration.queries.ts';
import {
    getIntegrationDescription,
    getIntegrationLogo
} from '@/modules/Integrations/Integrations.utils.ts';
import { valuesToSettings } from '@/pages/Integrations/Integrations.utils.ts';
// assets
import styles from '@/pages/Integrations/components/IntegrationItem/IntegrationItem.module.css';

interface IntegrationItemProps {
    integration: IIntegration;
}

const IntegrationItem = ({ integration }: IntegrationItemProps) => {
    const queryClient = useQueryClient();
    const { data: connectors = [], refetch } = useIntegrationConnectorsQuery(
        integration.id
    );
    const { data: settings = [] } = useIntegrationSettingsQuery(integration.id);
    const changeIntegrationSettingsMutation =
        useChangeIntegrationSettingsMutation();

    const handleAddConnector = () => {
        modals.open({
            title: 'Add Connector',
            size: '50%',
            children: (
                <AddConnector integration={integration} settings={settings} />
            )
        });
    };

    const handleEditSettingsSubmit = async (values: Record<string, unknown>) =>
        new Promise((resolve, reject) => {
            const data = valuesToSettings(values);
            changeIntegrationSettingsMutation.mutate(
                {
                    integrationId: integration.id,
                    data
                },
                {
                    onSuccess() {
                        queryClient.invalidateQueries({
                            queryKey: QUERY_KEYS.getIntegrationSettings(
                                integration.id
                            )
                        });
                        modals.closeAll();
                        resolve(true);
                    },
                    onError(error) {
                        reject(error);
                    }
                }
            );
        });

    const handleEditIntegration = () => {
        modals.open({
            title: `Edit ${integration.title} Integration`,
            size: '50%',
            children: (
                <SettingsForm
                    presets={integration.integration_settings_descriptions}
                    settings={settings}
                    onCancel={() => {
                        modals.closeAll();
                    }}
                    onSubmit={handleEditSettingsSubmit}
                />
            )
        });
    };

    const showDescription = () => {
        modals.open({
            title: integration.title,
            size: '50%',
            children: (
                <iframe
                    className={styles.iframe}
                    sandbox="allow-scripts allow-popups allow-forms"
                    src={getIntegrationDescription(integration.idn)}
                    title={integration.title}
                />
            )
        });
    };

    return (
        <>
            <Table.Tr
                className={clsx(
                    styles.integration,
                    integration.is_disabled && 'pointer-events-none opacity-40'
                )}
            >
                <Table.Td>
                    <div className="flex items-center gap-4">
                        <Avatar
                            alt={integration.title}
                            className="bg-white p-1"
                            radius="sm"
                            size="md"
                            src={getIntegrationLogo(integration.idn)}
                        >
                            {integration.idn.charAt(0).toUpperCase()}
                        </Avatar>
                        <div>
                            {integration.title}

                            <ActionIcon
                                className="ml-2 relative top-1"
                                color="blue"
                                size="sm"
                                variant="transparent"
                                onClick={showDescription}
                            >
                                <HelpCircle />
                            </ActionIcon>

                            <div className="text-xs leading-none text-gray-6">
                                Idn: {integration.idn}
                            </div>
                        </div>
                    </div>
                </Table.Td>
                <Table.Td>{connectors.length}</Table.Td>
                <Table.Td />
                <Table.Td>
                    <div className="flex justify-end gap-4">
                        {integration.integration_settings_descriptions.length >
                            0 && (
                            <UnstyledButton onClick={handleEditIntegration}>
                                <Settings />
                            </UnstyledButton>
                        )}
                        <UnstyledButton onClick={handleAddConnector}>
                            <Plus />
                        </UnstyledButton>
                    </div>
                </Table.Td>
            </Table.Tr>
            {connectors.map((connector) => (
                <ConnectorItem
                    connector={connector}
                    key={connector.id}
                    presets={integration.connector_settings_descriptions}
                    refetchConnectors={refetch}
                />
            ))}
        </>
    );
};

export default IntegrationItem;
